import type { CSSProperties, ReactNode } from 'react'
import {
  VpButton,
  VpModal,
  VpModalBody,
  VpModalCloseButton,
  VpModalDialog,
  VpModalFooter,
  VpModalHeader,
} from '@vtmn-play/react'
import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import './modal.scss'

export interface ModalProps {
  children: ReactNode
  dataTestid: string
  isOpen: boolean
  title: ReactNode
  width: string
  resetDisabled?: boolean
  validationDisabled?: boolean
  resetLabel?: ReactNode
  cancelLabel?: ReactNode
  validationLabel?: ReactNode
  validationVariant?: 'primary' | 'negative'
  onClose: () => void
  validationAction: () => void
  resetAction?: () => void
  cancelAction?: () => void
}

export function Modal({
  children,
  dataTestid,
  isOpen,
  title,
  width,
  resetDisabled,
  validationDisabled,
  resetLabel,
  cancelLabel,
  validationLabel,
  validationVariant = 'primary',
  onClose,
  resetAction,
  cancelAction,
  validationAction,
}: Readonly<ModalProps>) {
  const { t } = useTranslation()
  const hasResetAction = Boolean(resetAction)
  const triggerElementRef = useRef<HTMLElement | null>(null)

  const { trapRef, activate, deactivate, setReturnFocus } = useFocusTrap<HTMLDialogElement>({
    hideBackground: true,
  })

  useEffect(() => {
    if (isOpen) {
      triggerElementRef.current = document.activeElement as HTMLElement
      setReturnFocus(triggerElementRef.current)

      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        activate()
      }, 0)

      return () => clearTimeout(timer)
    }
    else {
      deactivate()
    }
  }, [isOpen, activate, deactivate, setReturnFocus])

  const modalFooterButtons = (
    <div
      className={clsx('modal-footer-buttons', {
        'with-reset': hasResetAction,
      })}
    >
      {resetAction
        && (
          <VpButton
            data-testid="modal_reset-button"
            variant="tertiary"
            size="medium"
            onClick={() => resetAction()}
            disabled={resetDisabled}
          >
            {resetLabel ?? t('reset-all')}
          </VpButton>
        )}
      <div>
        {cancelAction && (
          <VpButton
            data-testid="modal_cancel-button"
            variant="secondary"
            size="medium"
            onClick={() => cancelAction()}
          >
            {cancelLabel ?? t('cancel')}
          </VpButton>
        )}
        <VpButton
          data-testid="modal_action-button"
          variant={validationVariant}
          size="medium"
          disabled={validationDisabled}
          onClick={() => validationAction()}
        >
          {validationLabel ?? t('ok')}
        </VpButton>
      </div>
    </div>
  )

  return (
    <VpModal
      data-testid={dataTestid}
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      closeOnClickOutside
    >
      <VpModalDialog
        ref={trapRef}
        data-testid={dataTestid}
        aria-labelledby={`${dataTestid}-title`}
      >
        <VpModalHeader>
          <h2 id={`${dataTestid}-title`}>{title}</h2>
          <VpModalCloseButton aria-label={t('aria-label.close-modal')} />
        </VpModalHeader>
        <VpModalBody style={{ '--width': width } as CSSProperties}>
          {children}
        </VpModalBody>
        <VpModalFooter>{modalFooterButtons}</VpModalFooter>
      </VpModalDialog>
    </VpModal>
  )
}
